import * as React from 'react';
import { CURRENT_PRODUCT_MODE, PRODUCT_MODE_MAP } from '@utils/constants';
import { useFilterMeta } from '@src/components/filter/containers/hooks';
import {
  convertCustomTagToDimension,
  getDataSource,
  InvalidDimensionWarning,
  CUSTOM_TAG_WARNING_CONTENT,
  type InvalidDimensionWarningProps,
} from '@src/components/filter/components';
import { useSavedFilterErrorMessage } from '@src/components/summary-panel/FilterDropdown/FilterStatus';
import type { FilterRule } from '@src/components/filter/types';

// Check if the filter contains invalid custom tag
const checkInvalidCustomTagFilter = (rule: FilterRule[][]) => {
  if (!['EI', 'AI'].includes(CURRENT_PRODUCT_MODE)) {
    return false;
  }

  return rule.flat(2).some((it) => it.field === 'TAG');
};

const checkInvalidDimensions = (dimensions: Set<string>, rule: FilterRule[][]) =>
  rule.flat(2).some((it) => !dimensions.has(it.field));

// This hook only checks EI and AI, since DPI has separeted post check which is handled by MGW Mario response
// TODO: should be refactored to use the same logic as DPI
const FILTER_META_ENABLED = [
  PRODUCT_MODE_MAP.EI,
  PRODUCT_MODE_MAP.AI,
  // PRODUCT_MODE_MAP.APP,
].includes(CURRENT_PRODUCT_MODE);

export const useInvalidDimensionCheck = () => {
  const { data: meta, isLoading } = useFilterMeta(getDataSource(), {
    enabled: FILTER_META_ENABLED,
  });
  const filterDimensionSet = React.useMemo(() => {
    if (!FILTER_META_ENABLED || isLoading || !meta) return undefined;

    return new Set(
      meta.dimensions
        .map((it) => it.fields)
        .flat(2)
        .map((it) => it.value),
    );
  }, [isLoading, meta]);
  const savedFilterErrorMessage = useSavedFilterErrorMessage();

  return React.useCallback(
    (rule: FilterRule[][], componentProps?: InvalidDimensionWarningProps) => {
      if (!filterDimensionSet) return null;

      const convertedRule = convertCustomTagToDimension(filterDimensionSet, rule);

      if (checkInvalidCustomTagFilter(convertedRule))
        return <InvalidDimensionWarning title={CUSTOM_TAG_WARNING_CONTENT} {...componentProps} />;

      if (checkInvalidDimensions(filterDimensionSet, convertedRule))
        return <InvalidDimensionWarning title={savedFilterErrorMessage} {...componentProps} />;

      return null;
    },
    [filterDimensionSet, savedFilterErrorMessage],
  );
};
